import { api } from '@goaltree/backend/convex/_generated/api'
import { cn } from '@goaltree/ui/lib/utils'
import { Link, useNavigate } from '@tanstack/react-router'
import { useMutation, useQuery } from 'convex/react'
import type { FunctionReturnType } from 'convex/server'
import { ConvexError } from 'convex/values'
import { Check, Flag, Leaf, Medal, MessageSquare, Sprout, Trophy } from 'lucide-react'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { CanopyAvatar } from '@/components/canopy-avatar'
import { LeaderboardUserSheet } from '@/components/leaderboard-user-sheet'
import { StreakFlame } from '@/components/streak-icons'
import { UserAvatar } from '@/components/user-avatar'
import { CATEGORY_COLORS, useCategoryFill } from '@/lib/category-color'
import { goalSlug } from '@/lib/utils'

type Feed = FunctionReturnType<typeof api.activity.getFeed>
type Card = Feed['cards'][number]
type BoostKind = 'water' | 'sun' | 'fire' | 'summit'

const BOOSTS: { kind: BoostKind; emoji: string }[] = [
  { kind: 'water', emoji: '💧' },
  { kind: 'sun', emoji: '☀️' },
  { kind: 'fire', emoji: '🔥' },
  { kind: 'summit', emoji: '🏔️' },
]
const EMOJI = Object.fromEntries(BOOSTS.map((b) => [b.kind, b.emoji])) as Record<BoostKind, string>
const KIND_ICON: Record<Card['kind'], ReactNode> = {
  habit: <Sprout className="size-3.5 text-primary-text" />,
  task: <Check className="size-3.5 text-primary-text" />,
  milestone: <Flag className="size-3.5 text-primary-text" />,
  goal: <Trophy className="size-3.5 text-amber-500" />,
  streak: <StreakFlame />,
  badge: <Medal className="size-3.5 text-amber-500" />,
  level: <Leaf className="size-3.5 text-primary-text" />,
  post: <MessageSquare className="size-3.5 text-primary-text" />,
}

function ago(ms: number, locale: string) {
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto', style: 'narrow' })
  const mins = Math.round((Date.now() - ms) / 60_000)
  if (mins < 60) return rtf.format(-Math.max(mins, 1), 'minute')
  const hours = Math.round(mins / 60)
  if (hours < 24) return rtf.format(-hours, 'hour')
  return rtf.format(-Math.round(hours / 24), 'day')
}

type Person = { userId: string; name: string; image: string | null }

export function MomentumFeed() {
  const [pages, setPages] = useState(1)
  const [person, setPerson] = useState<Person | null>(null)
  return (
    <>
      <FeedPage
        depth={0}
        pages={pages}
        onMore={() => setPages((n) => n + 1)}
        onPerson={setPerson}
      />
      <LeaderboardUserSheet
        userId={person?.userId ?? null}
        name={person?.name ?? ''}
        image={person?.image ?? null}
        onClose={() => setPerson(null)}
      />
    </>
  )
}

// Pages chain by cursor: each page's last event seeds the next, so a new event on
// page 1 shifts the later pages instead of leaving gaps or duplicates.
function FeedPage({
  before,
  depth,
  pages,
  onMore,
  onPerson,
}: {
  before?: number
  depth: number
  pages: number
  onMore: () => void
  onPerson: (p: Person) => void
}) {
  const data = useQuery(api.activity.getFeed, before === undefined ? {} : { before })
  if (!data) return <MomentumSkeleton count={depth === 0 ? 4 : 2} />
  if (depth === 0 && data.cards.length === 0) return <EmptyState feed={data} />
  return (
    <>
      {depth === 0 && !data.hasHandle && <HiddenNotice />}
      <div className="grid grid-cols-[minmax(0,1fr)] gap-3">
        {data.cards.map((card) => (
          <MomentumCard key={card.id} card={card} onPerson={onPerson} />
        ))}
      </div>
      {data.nextBefore !== null &&
        (depth + 1 < pages ? (
          <div className="mt-3">
            <FeedPage
              before={data.nextBefore}
              depth={depth + 1}
              pages={pages}
              onMore={onMore}
              onPerson={onPerson}
            />
          </div>
        ) : (
          <LoadMore onMore={onMore} />
        ))}
    </>
  )
}

function LoadMore({ onMore }: { onMore: () => void }) {
  const { t } = useTranslation()
  const ref = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) onMore()
    })
    io.observe(el)
    return () => io.disconnect()
  }, [onMore])
  return (
    <button
      ref={ref}
      type="button"
      onClick={onMore}
      className="mx-auto mt-4 block cursor-pointer rounded-full px-4 py-2 text-xs font-semibold text-muted-foreground hover:text-foreground"
    >
      {t('momentum.loadMore')}
    </button>
  )
}

function useBoost() {
  const { t } = useTranslation()
  const mutate = useMutation(api.activity.boost).withOptimisticUpdate(
    (store, { eventId, kind }) => {
      for (const { args, value } of store.getAllQueries(api.activity.getFeed)) {
        if (!value?.cards.some((c) => c.id === eventId)) continue
        store.setQuery(api.activity.getFeed, args, {
          ...value,
          cards: value.cards.map((c) => {
            if (c.id !== eventId) return c
            const counts = { ...c.counts }
            const next = c.myBoost === kind ? null : kind
            if (c.myBoost) counts[c.myBoost]--
            if (next) counts[next]++
            return {
              ...c,
              counts,
              myBoost: next,
              boostCount: c.boostCount + (next ? 1 : 0) - (c.myBoost ? 1 : 0),
            }
          }),
        })
      }
    },
  )
  return (eventId: Card['id'], kind: BoostKind) =>
    mutate({ eventId, kind }).catch((err) => {
      if (!(err instanceof ConvexError)) toast.error(t('common.error'))
    })
}

function eventLine(card: Card, t: ReturnType<typeof useTranslation>['t']) {
  switch (card.kind) {
    case 'habit':
      return t('momentum.event.habit', { count: card.habits.length })
    case 'streak':
      return t('momentum.event.streak', { count: card.streak ?? 0 })
    case 'level':
      return t('momentum.event.level', { level: card.level ?? 0 })
    default:
      return t(`momentum.event.${card.kind}`)
  }
}

function MomentumCard({ card, onPerson }: { card: Card; onPerson: (p: Person) => void }) {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const fill = useCategoryFill()
  const boost = useBoost()
  const color = fill(CATEGORY_COLORS[card.category?.toLowerCase() ?? ''] ?? CATEGORY_COLORS.mind)
  const title =
    card.kind === 'habit' ? card.habits.join(', ') : card.kind === 'streak' ? null : card.title
  const showGoal = card.goalTitle && card.goalId && card.kind !== 'goal'
  const name = card.isMe ? t('momentum.you') : card.name
  const openPerson = () => onPerson({ userId: card.userId, name: card.name, image: card.image })

  return (
    <article className="min-w-0 rounded-2xl border border-border/60 bg-card px-3.5 pt-3 pb-2.5">
      <div className="flex gap-3">
        <button type="button" onClick={openPerson} className="shrink-0 cursor-pointer">
          <CanopyAvatar userId={card.userId}>
            <UserAvatar
              image={card.image}
              name={card.name}
              size={36}
              className="size-9 rounded-full bg-muted object-cover"
            />
          </CanopyAvatar>
        </button>
        <div className="min-w-0 flex-1">
          <p className="text-[13px] leading-snug text-muted-foreground">
            <button
              type="button"
              onClick={openPerson}
              className="cursor-pointer font-semibold text-foreground hover:underline"
            >
              {name}
            </button>{' '}
            {eventLine(card, t)}
            <span className="ml-1.5 font-mono text-[11px] text-muted-foreground/70">
              {ago(card.createdAt, i18n.language)}
            </span>
          </p>
          {title && (
            <p className="mt-0.5 flex min-w-0 items-baseline gap-1.5 text-sm font-semibold text-foreground">
              <span aria-hidden className="shrink-0 self-center text-[13px]">
                {KIND_ICON[card.kind]}
              </span>
              <span
                className={
                  card.kind === 'post'
                    ? 'line-clamp-6 font-normal whitespace-pre-wrap [overflow-wrap:anywhere]'
                    : 'line-clamp-2 [overflow-wrap:anywhere]'
                }
              >
                {title}
              </span>
            </p>
          )}
          {card.kind === 'streak' && (
            <p className="mt-0.5 text-2xl font-black text-foreground">
              <StreakFlame /> {t('momentum.streakDays', { count: card.streak ?? 0 })}
            </p>
          )}
          {showGoal && (
            <button
              type="button"
              onClick={() =>
                navigate({
                  to: '/friends/goals/$goalId',
                  params: { goalId: goalSlug(card.goalTitle!, card.goalId!) },
                })
              }
              className="mt-1.5 flex w-full cursor-pointer items-center gap-2 text-left text-[11px] text-muted-foreground hover:text-foreground"
            >
              <span className="size-[7px] shrink-0 rounded-full" style={{ background: color }} />
              <span className="min-w-0 truncate">{card.goalTitle}</span>
              {card.goalPct !== null && (
                <>
                  <span className="ml-auto h-[3px] w-full max-w-[110px] shrink-0 basis-[70px] overflow-hidden rounded-full bg-muted">
                    <span
                      className="block h-full rounded-full"
                      style={{ width: `${card.goalPct}%`, background: color }}
                    />
                  </span>
                  <span className="shrink-0 font-mono text-[10.5px] font-medium">
                    {card.goalPct}%
                  </span>
                </>
              )}
            </button>
          )}
        </div>
      </div>
      <BoostRow card={card} onBoost={(kind) => boost(card.id, kind)} onPerson={onPerson} />
    </article>
  )
}

function BoostRow({
  card,
  onBoost,
  onPerson,
}: {
  card: Card
  onBoost: (kind: BoostKind) => void
  onPerson: (p: Person) => void
}) {
  const { t } = useTranslation()
  const summitable = card.kind === 'milestone' || card.kind === 'goal'
  const kinds = BOOSTS.filter(
    (b) => (b.kind !== 'summit' || summitable) && (!card.isMe || card.counts[b.kind] > 0),
  )
  const [first] = card.boosters
  return (
    <div className="mt-2.5 flex items-center gap-1 pl-12">
      {kinds.map((b) => (
        <BoostButton
          key={b.kind}
          kind={b.kind}
          count={card.counts[b.kind]}
          active={card.myBoost === b.kind}
          readOnly={card.isMe}
          onPress={() => onBoost(b.kind)}
        />
      ))}
      {first && (
        <button
          type="button"
          onClick={() => onPerson(first)}
          className="ml-auto flex min-w-0 cursor-pointer items-center gap-1.5 text-[11px] text-muted-foreground hover:text-foreground"
        >
          <span className="flex shrink-0 -space-x-1.5">
            {card.boosters.slice(0, 3).map((b) => (
              <span key={b.userId} className="relative">
                <UserAvatar
                  image={b.image}
                  name={b.name}
                  size={20}
                  className="size-5 rounded-full bg-muted object-cover ring-2 ring-card"
                />
                <span className="absolute -right-1 -bottom-1 text-[9px] leading-none">
                  {EMOJI[b.kind]}
                </span>
              </span>
            ))}
          </span>
          <span className="truncate">
            {card.boostCount > 1
              ? t('momentum.boostedByMany', { name: first.name, count: card.boostCount - 1 })
              : first.name}
          </span>
        </button>
      )}
    </div>
  )
}

function BoostButton({
  kind,
  count,
  active,
  readOnly,
  onPress,
}: {
  kind: BoostKind
  count: number
  active: boolean
  readOnly: boolean
  onPress: () => void
}) {
  const { t } = useTranslation()
  const reduce = useReducedMotion()
  const [pops, setPops] = useState(0)
  return (
    <button
      type="button"
      disabled={readOnly}
      aria-pressed={active}
      aria-label={t(`momentum.boost.${kind}`)}
      title={t(`momentum.boost.${kind}`)}
      onClick={() => {
        if (!active) setPops((n) => n + 1)
        onPress()
      }}
      className={cn(
        'relative flex h-7 items-center gap-1 rounded-full border px-2 text-xs transition-colors',
        readOnly ? 'cursor-default' : 'cursor-pointer',
        active
          ? 'border-primary/40 bg-primary/10 text-primary-text'
          : 'border-border/60 text-muted-foreground',
        !readOnly && !active && 'hover:bg-muted/40 hover:text-foreground',
      )}
    >
      <motion.span
        key={pops}
        initial={reduce || pops === 0 ? false : { scale: 0.6 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', stiffness: 600, damping: 12 }}
        className="text-[13px] leading-none"
      >
        {EMOJI[kind]}
      </motion.span>
      {count > 0 && <span className="font-mono text-[11px] font-semibold">{count}</span>}
      <AnimatePresence>
        {pops > 0 && !reduce && (
          <motion.span
            key={pops}
            aria-hidden
            initial={{ opacity: 1, y: 0, scale: 0.8 }}
            animate={{ opacity: 0, y: -22, scale: 1.3 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            className="pointer-events-none absolute left-1.5 text-sm"
          >
            {EMOJI[kind]}
          </motion.span>
        )}
      </AnimatePresence>
    </button>
  )
}

function HiddenNotice() {
  const { t } = useTranslation()
  return (
    <div className="mb-3 rounded-xl border border-dashed border-border px-3.5 py-2.5 text-xs text-muted-foreground">
      {t('momentum.hiddenNotice')}{' '}
      <Link to="/settings" className="font-semibold text-primary-text hover:underline">
        {t('momentum.hiddenNoticeCta')}
      </Link>
    </div>
  )
}

function EmptyState({ feed }: { feed: Feed }) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="mb-4 flex size-14 items-center justify-center rounded-2xl bg-muted/40">
        <Sprout className="size-6 text-muted-foreground/50" />
      </div>
      <p className="text-sm font-medium text-foreground/70">
        {feed.friendCount === 0 ? t('momentum.emptyNoFriends') : t('momentum.empty')}
      </p>
      <p className="mt-1 max-w-xs text-xs text-muted-foreground/70">
        {feed.friendCount === 0 ? t('momentum.emptyNoFriendsDesc') : t('momentum.emptyDesc')}
      </p>
      <div className="mt-5 flex gap-2">
        <Link
          to="/leaderboard"
          className="rounded-full bg-primary px-4 py-2 text-xs font-bold text-primary-foreground"
        >
          {t('momentum.findPeople')}
        </Link>
        <Link
          to="/explore"
          className="rounded-full border border-border px-4 py-2 text-xs font-semibold text-foreground hover:bg-muted/40"
        >
          {t('momentum.explore')}
        </Link>
      </div>
      {!feed.hasHandle && (
        <p className="mt-6 max-w-xs text-[11px] text-muted-foreground/70">
          {t('momentum.hiddenNotice')}{' '}
          <Link to="/settings" className="font-semibold text-primary-text hover:underline">
            {t('momentum.hiddenNoticeCta')}
          </Link>
        </p>
      )}
    </div>
  )
}

export function MomentumSkeleton({ count = 4 }: { count?: number }) {
  return (
    <div className="grid gap-3">
      {[62, 48, 70, 54].slice(0, count).map((w) => (
        <div key={w} className="rounded-2xl border border-border/60 bg-card px-3.5 pt-3 pb-2.5">
          <div className="flex gap-3">
            <div className="size-9 shrink-0 animate-pulse rounded-full bg-muted" />
            <div className="min-w-0 flex-1">
              <div className="h-3 animate-pulse rounded bg-muted/60" style={{ width: `${w}%` }} />
              <div
                className="mt-2 h-3.5 animate-pulse rounded bg-muted"
                style={{ width: `${w - 14}%` }}
              />
              <div className="mt-2.5 flex items-center gap-2">
                <div className="size-[7px] rounded-full bg-muted" />
                <div className="h-2.5 w-24 animate-pulse rounded bg-muted/60" />
                <div className="ml-auto h-[3px] w-[70px] rounded-full bg-muted" />
              </div>
            </div>
          </div>
          <div className="mt-2.5 flex gap-1 pl-12">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-7 w-10 animate-pulse rounded-full bg-muted/50" />
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}
